import React, { Fragment, memo } from 'react'
import tw from 'twin.macro'
import { Link } from 'react-router-dom'
import { Tooltip } from '@mui/material'
import type { Crumb } from 'src/constants/adminApps.types'
import { truncateMiddle } from 'src/utils/truncateMiddle'

interface Props {
  crumbs: Crumb[]
}

/**
 * Longest label shown whole. App and tab titles are all shorter; what this catches is the
 * customer-name crumb, whose test-fixture names run to 40+ characters.
 */
const MAX_LABEL_LENGTH = 24

/**
 * Route trail for the white top bar: crumbs with a path as grey links, a crumb without one as
 * plain black text — that is the page the admin is already on, so it alone carries
 * `aria-current`. A label longer than `MAX_LABEL_LENGTH` is cut in the middle — the suffix is
 * usually what tells two similar names apart — and the full label is shown on hover.
 */
const BreadcrumbComponent = ({ crumbs }: Props) => {
  return (
    <nav
      aria-label="Breadcrumb"
      data-testid="admin-breadcrumb"
      css={tw`flex min-w-0 flex-1 items-center gap-1 overflow-hidden`}
    >
      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1
        const shownLabel = truncateMiddle(crumb.label, MAX_LABEL_LENGTH)
        const isTruncated = shownLabel !== crumb.label

        // Only a crumb with no path is the page the admin is already on. Position says nothing:
        // a caller may drop the current page from the trail, and the deepest crumb left is then an
        // ancestor that must stay a link and must not claim `aria-current`.
        const element = !crumb.path ? (
          <span
            aria-current="page"
            aria-label={isTruncated ? crumb.label : undefined}
            css={tw`shrink-0 whitespace-nowrap text-sm font-medium text-shades-1000`}
          >
            {shownLabel}
          </span>
        ) : (
          <Link
            to={crumb.path}
            aria-label={isTruncated ? crumb.label : undefined}
            css={tw`shrink-0 whitespace-nowrap text-sm font-medium text-shades-500 no-underline hover:text-shades-1000 hover:underline`}
          >
            {shownLabel}
          </Link>
        )

        return (
          <Fragment key={`${crumb.label}-${index}`}>
            {isTruncated ? (
              <Tooltip title={crumb.label} placement="bottom">
                {element}
              </Tooltip>
            ) : (
              element
            )}
            {!isLast && <span css={tw`shrink-0 text-sm text-shades-300`}>/</span>}
          </Fragment>
        )
      })}
    </nav>
  )
}

export const Breadcrumb = memo(BreadcrumbComponent)
